"use client";

import React, { useState } from "react";
import * as z from "zod";

import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/shadcn/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";

import PlanCard from "@/components/plan-card";
import { Button } from "@/components/shadcn/button";
import { Input } from "@/components/shadcn/input";
import { cn } from "@/lib/utils";
import useStore from "@/store/useStore";
import { useTranslations } from "next-intl";
import { useRouter } from "next/navigation";

// Messages come from the `checkout` catalog, so the schema is built per render.
const buildFormSchema = (t: (key: string) => string) =>
    z.object({
        amount: z
            .number()
            .min(1, { message: t("peopleCountRequired") })
            .max(20),
    });

type ValidationSchema = z.infer<ReturnType<typeof buildFormSchema>>;

export default function AskidaForm({ products }: { products: any }) {
    const t = useTranslations("checkout");
    const formSchema = buildFormSchema(t);
    const { personalInfo, setPersonalInfo, increaseStep, product, setProduct } =
        useStore((state) => state);

    const [selectedPlan, setSelectedPlan] = useState<any>(null);
    const router = useRouter();
    const form = useForm<ValidationSchema>({
        resolver: zodResolver(formSchema),
        defaultValues: { amount: 1 },
    });
    const {
        control,
        formState: { errors },
    } = form;

    const [amount, setAmount] = useState(1);
    const [price, setPrice] = useState(parseInt(products[0].Price));
    let [totalPrice, setTotalPrice] = useState(0);
    const productBasketType: { [key: string]: string } = {
        1: "subscriptionOrder",
        2: "basketOrder",
        3: "basketOrder",
        4: "basketOrder",
        5: "donationOrder",
        6: "giftOrder",
    };

    const onSubmit = (values: ValidationSchema) => {
        setProduct({
            ...product,
            Id: selectedPlan ? selectedPlan.Id : products[0].Id,
            Title: selectedPlan ? selectedPlan.Title : products[0].Title,
            Price: selectedPlan ? selectedPlan.Price : products[0].Price,
            Type: "donationOrder",
            Duration: selectedPlan
                ? selectedPlan.Duration
                : products[0].Duration,
            Amount: amount ? amount : 1,
        });

        router.push("/premium/checkout");
    };

    const handleOnClick = (plan: any) => {
        setAmount(amount);

        setPrice(plan.Price);
        setTotalPrice(plan.Price * (amount ? amount : 1));

        setSelectedPlan({
            ...selectedPlan,
            Id: plan.Id,
            Title: plan.Title,
            Price: plan.Price,
            Duration: plan.Duration,
        });
        setProduct({
            ...product,
            Id: plan.Id,
            Title: plan.Title,
            Price: plan.Price,
            Type: "donationOrder",
            Amount: amount,
            Duration: plan.Duration,
        });
    };

    const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setAmount(parseInt(e.target.value));
        setProduct({
            ...product,

            Type: "donationOrder",
            Amount: parseInt(e.target.value),
            // selectedPlan is null until a plan card is clicked; changing the
            // amount first must fall back to the default product (matches onSubmit).
            Duration: selectedPlan
                ? selectedPlan.Duration
                : products[0].Duration,
        });

        setPrice(price);

        setTotalPrice(price * amount);
    };

    totalPrice = amount * price;

    return (
        <Form {...form}>
            <form
                className="flex flex-col"
                onSubmit={form.handleSubmit(onSubmit)}
            >
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {products.map((plan: any) => (
                        <PlanCard
                            key={`Products-${plan.Id}`}
                            onClick={() => handleOnClick(plan)}
                            item={plan}
                            selectedPlan={selectedPlan}
                        />
                    ))}
                </div>

                <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                        <FormField
                            control={control}
                            name="amount"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="flex items-center justify-between">
                                        {t("peopleCount")}
                                        <FormMessage>
                                            {errors.amount?.message}
                                        </FormMessage>
                                    </FormLabel>
                                    <FormControl>
                                        <Input
                                            type="number"
                                            className={cn(
                                                "placeholder:font-medium",
                                                {
                                                    "border-destructive":
                                                        errors.amount?.message,
                                                },
                                            )}
                                            {...field}
                                            value={amount ? amount : 1}
                                            onChange={(e) =>
                                                handleAmountChange(e)
                                            }
                                            min="1"
                                        />
                                    </FormControl>
                                </FormItem>
                            )}
                        />
                    </div>
                </div>
                <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-baseline gap-1">
                        <span>Toplam Fiyat:</span>
                        <span className="text-2xl font-bold">
                            {totalPrice} TL
                        </span>
                    </div>
                    <Button
                        onClick={form.handleSubmit(onSubmit)}
                        className="w-40 justify-center"
                    >
                        Hediye Edin!
                    </Button>
                </div>
            </form>
        </Form>
    );
}
